import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Loader2, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../lib/api';

/**
 * A compact 7-day strip visualising workload per day. Role-agnostic UI: the
 * backend returns the same { date, visits, hours, missed, ... } shape for
 * both aides (`/aide/my-week`) and nurses/physicians (`/nurse/my-week`), and
 * we pick two role-appropriate metrics to render.
 *
 * Props:
 *   role:          'aide' | 'nurse' (drives the endpoint + labels)
 *   selectedDate:  YYYY-MM-DD — the date highlighted in the strip
 *   onSelectDate:  callback(ymd) — fired when the user taps a day
 *
 * The strip auto-shifts 7 days back/forward via the chevron buttons. Whenever
 * the selected date falls outside the visible window we re-anchor the end
 * date to include it.
 */
export default function MyWeekStrip({ role, selectedDate, onSelectDate }) {
  const { t } = useTranslation();
  const endpoint = role === 'aide' ? '/aide/my-week' : '/nurse/my-week';
  const today = new Date().toISOString().slice(0, 10);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Re-anchor the strip if `selectedDate` drifts outside the 7-day window.
  useEffect(() => {
    if (!selectedDate || !data) return;
    const inRange = data.days.some(d => d.date === selectedDate);
    if (!inRange) setEndDate(selectedDate);
  }, [selectedDate, data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get(endpoint, { params: { end_date: endDate } });
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData({ days: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, endDate]);

  const shift = (delta) => {
    const d = new Date(`${endDate}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setEndDate(d.toISOString().slice(0, 10));
  };

  const totalHours = (data?.days || []).reduce((s, d) => s + (d.hours || 0), 0);
  const totalVisits = (data?.days || []).reduce((s, d) => s + (d.visits || 0), 0);
  const totalMissed = (data?.days || []).reduce((s, d) => s + (d.missed || 0), 0);
  const totalTasksDone = (data?.days || []).reduce((s, d) => s + (d.tasks_completed || 0), 0);

  return (
    <Card className="careiq-card" data-testid="my-week-strip">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#0E7490]" />
            <span className="font-semibold text-sm text-[#0A2342]">My Week</span>
            {data && (
              <span className="text-[11px] text-[#64748B] ml-1">
                {new Date(`${data.start_date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(`${data.end_date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => shift(-7)}
              className="w-7 h-7 rounded border border-[#E2E8F0] hover:bg-[#F1F5F9] flex items-center justify-center"
              data-testid="my-week-prev"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-3.5 h-3.5 text-[#64748B]" />
            </button>
            <button
              onClick={() => shift(7)}
              className="w-7 h-7 rounded border border-[#E2E8F0] hover:bg-[#F1F5F9] flex items-center justify-center"
              data-testid="my-week-next"
              aria-label="Next week"
            >
              <ChevronRight className="w-3.5 h-3.5 text-[#64748B]" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-4 h-4 text-[#0E7490] animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1.5" data-testid="my-week-days">
              {(data?.days || []).map((d) => {
                const dt = new Date(`${d.date}T12:00:00`);
                const isSelected = d.date === selectedDate;
                const isToday = d.date === today;
                const weekday = dt.toLocaleDateString('en-US', { weekday: 'short' });
                const dayNum = dt.getDate();
                const primary = role === 'aide' ? d.hours : (d.tasks_completed || 0);
                const primaryLabel = role === 'aide' ? (d.hours ? `${d.hours}h` : '0h') : `${d.tasks_completed || 0}`;
                const secondary = d.visits || d.appointments || 0;
                const missed = d.missed || 0;
                return (
                  <button
                    key={d.date}
                    onClick={() => onSelectDate?.(d.date)}
                    data-testid={`my-week-day-${d.date}`}
                    className={`relative rounded-lg px-1.5 py-2 text-center transition-all border ${
                      isSelected
                        ? 'bg-[#0E7490] text-white border-[#0E7490] shadow-sm'
                        : isToday
                        ? 'bg-[#F0FDFA] border-[#0E7490]/30 hover:border-[#0E7490]'
                        : 'bg-white border-[#E2E8F0] hover:border-[#0E7490]/40'
                    }`}
                  >
                    <div className={`text-[9px] uppercase tracking-wide ${isSelected ? 'text-white/80' : 'text-[#64748B]'}`}>
                      {weekday}
                    </div>
                    <div className={`text-base font-bold ${isSelected ? 'text-white' : 'text-[#0A2342]'}`}>
                      {dayNum}
                    </div>
                    <div className={`text-[10px] font-medium mt-0.5 ${isSelected ? 'text-white' : primary ? 'text-[#0E7490]' : 'text-[#94A3B8]'}`}>
                      {primaryLabel}
                    </div>
                    <div className={`text-[9px] ${isSelected ? 'text-white/80' : 'text-[#94A3B8]'}`}>
                      {secondary} {role === 'aide' ? 'vis' : 'appt'}
                    </div>
                    {missed > 0 && (
                      <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#991B1B] text-white text-[9px] font-bold flex items-center justify-center" data-testid={`my-week-missed-${d.date}`}>
                        {missed}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Totals strip */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F1F5F9] text-[11px]">
              {role === 'aide' ? (
                <>
                  <span className="text-[#64748B]">Total hours: <span className="font-semibold text-[#0A2342]" data-testid="my-week-total-hours">{totalHours.toFixed(2)}h</span></span>
                  <span className="text-[#64748B]">Visits: <span className="font-semibold text-[#0A2342]" data-testid="my-week-total-visits">{totalVisits}</span></span>
                  {totalMissed > 0 && (
                    <span className="text-[#991B1B]">Missed: <span className="font-semibold" data-testid="my-week-total-missed">{totalMissed}</span></span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-[#64748B]">{t('dashboard.tasksDone')}: <span className="font-semibold text-[#0A2342]" data-testid="my-week-total-tasks">{totalTasksDone}</span></span>
                  <span className="text-[#64748B]">{t('dashboard.appts')}: <span className="font-semibold text-[#0A2342]" data-testid="my-week-total-appts">{totalVisits}</span></span>
                  {totalMissed > 0 && (
                    <span className="text-[#991B1B]">No-shows: <span className="font-semibold" data-testid="my-week-total-missed">{totalMissed}</span></span>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
